<template>
	<div class="w-full lg:w-1/2 mx-auto px-3 sm:px-6 lg:px-0 py-3 sm:py-6">
		<ViewInvoice />
		<InvoicePayment />
		<div
			class="flex items-center justify-between p-3 sm:p-6 bg-primary shadow-md rounded-t-xl"
		>
			<h1 class="text-lg sm:text-2xl font-bold text-white">
				Policy Renewal
			</h1>
		</div>

		<div
			v-if="selectedProduct.length"
			v-for="product in selectedProduct"
			:key="product.policyNumber"
			class="p-3 sm:p-6 lg:p-8 bg-white rounded-b-xl shadow-lg border border-gray-100 space-y-4 sm:space-y-6"
		>
			<div class="bg-surface rounded-lg p-3 sm:p-5 space-y-1">
				<h2
					class="text-sm sm:text-lg font-bold text-primary break-words"
				>
					Policy: {{ product.policyNumber }}
				</h2>
				<div v-for="client in product.client_details">
					<p class="text-xs text-text-secondary break-all">
						<span class="font-semibold text-brand-cyan">{{
							client.email
						}}</span>
					</p>
				</div>
			</div>

			<div
				class="grid grid-cols-2 gap-2 sm:gap-4 bg-surface rounded-lg p-3 sm:p-5 text-xs sm:text-sm"
			>
				<div>
					<p class="text-text-primary font-semibold">Type</p>
					<p class="text-text-secondary">{{ product.policyType }}</p>
				</div>
				<div>
					<p class="text-text-primary font-semibold">Status</p>
					<p class="text-brand-cyan font-semibold">
						{{ product.policyStatus }}
					</p>
				</div>
				<div>
					<p class="text-text-primary font-semibold">Channel</p>
					<p class="text-text-secondary">{{ product.channel }}</p>
				</div>
				<div>
					<p class="text-text-primary font-semibold">Currency</p>
					<p class="text-text-secondary">
						{{ product.quotationCurrency }}
					</p>
				</div>
				<div v-for="p in product.product_details" class="col-span-2">
					<p class="text-text-primary font-semibold">Product</p>
					<p class="text-primary font-semibold">
						{{ p.productName }}
					</p>
				</div>
			</div>

			<div class="space-y-2 sm:space-y-4">
				<h3
					class="text-xs sm:text-base font-bold text-primary pb-1 sm:pb-2 border-b-2 border-brand-cyan"
				>
					Vehicle Details
				</h3>
				<div
					class="grid grid-cols-2 gap-2 sm:gap-4 bg-surface rounded-lg p-3 sm:p-5 text-xs sm:text-sm"
					v-for="vehicle in product.risks"
				>
					<div>
						<p class="text-text-primary font-semibold">
							Reg Number
						</p>
						<p class="text-text-secondary">{{ vehicle.riskRef }}</p>
					</div>
					<div>
						<p class="text-text-primary font-semibold">Value</p>
						<p class="text-text-secondary">{{ vehicle.value }}</p>
					</div>
				</div>
			</div>

			<div v-if="product.quotationPremium" class="space-y-2 sm:space-y-4">
				<h3
					class="text-xs sm:text-base font-bold text-primary pb-1 sm:pb-2 border-b-2 border-brand-cyan"
				>
					Premium Details
				</h3>
				<div
					class="grid grid-cols-2 gap-2 sm:gap-4 bg-surface rounded-lg p-3 sm:p-5 text-xs sm:text-sm"
				>
					<div>
						<p class="text-text-primary font-semibold">
							Base Premium
						</p>
						<p class="text-primary font-semibold">
							{{
								formatCurrency(
									product.quotationPremium.basePremium,
								)
							}}
						</p>
					</div>
					<div>
						<p class="text-text-primary font-semibold">
							Total Premium
						</p>
						<p class="text-accent font-semibold">
							{{
								formatCurrency(
									product.quotationPremium.totalPremium,
								)
							}}
						</p>
					</div>
				</div>
			</div>

			<div class="space-y-3 sm:space-y-5 pt-2">
				<div
					class="bg-white rounded-xl p-3 sm:p-6 border-2 border-primary shadow-md"
				>
					<div class="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-5">
						<div
							class="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-lg flex items-center justify-center shrink-0"
						>
							<svg
								class="w-4 h-4 sm:w-5 sm:h-5 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
						</div>
						<h3 class="text-xs sm:text-base font-bold text-primary">
							Start Date
						</h3>
					</div>
					<input
						id="start_date"
						type="date"
						v-model="start_date"
						@blur="validateField('start_date')"
						:min="todayDate"
						class="w-full px-3 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 bg-surface focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan outline-none transition-all"
						:class="{ 'border-accent': errors.start_date }"
					/>
					<p
						v-if="errors.start_date"
						class="text-xs text-accent font-medium mt-1.5"
					>
						{{ errors.start_date }}
					</p>
				</div>

				<div
					class="bg-white rounded-xl p-3 sm:p-6 border-2 border-primary shadow-md"
				>
					<div class="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-5">
						<div
							class="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-lg flex items-center justify-center shrink-0"
						>
							<svg
								class="w-4 h-4 sm:w-5 sm:h-5 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
								/>
							</svg>
						</div>
						<h3 class="text-xs sm:text-base font-bold text-primary">
							Remarks
						</h3>
					</div>
					<textarea
						v-model="remarks"
						@blur="validateField('remarks')"
						class="w-full h-20 sm:h-32 px-3 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 bg-surface focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan outline-none transition-all resize-none"
						:class="{ 'border-accent': errors.remarks }"
						placeholder="Provide details..."
					></textarea>
					<p
						v-if="errors.remarks"
						class="text-xs text-accent font-medium mt-1.5"
					>
						{{ errors.remarks }}
					</p>
				</div>
			</div>

			<div class="flex flex-col gap-2 sm:gap-3 pt-1">
				<button
					v-if="!requestMade"
					class="w-full bg-primary text-white py-2.5 sm:py-3.5 px-6 text-sm sm:text-base rounded-lg font-semibold hover:bg-primary-light shadow-md hover:shadow-lg transition-all duration-200 active:scale-98"
					@click="initiatePayment"
					:disabled="loading"
				>
					{{ loading ? 'Processing...' : 'Renew Policy' }}
				</button>

				<button
					v-if="requestMade"
					class="w-full bg-green-600 text-white py-2.5 sm:py-3.5 px-6 text-sm sm:text-base rounded-lg font-semibold hover:bg-green-700 shadow-md hover:shadow-lg transition-all duration-200 active:scale-98"
					@click="acceptRenewal"
					:disabled="loading"
				>
					{{ loading ? 'Processing...' : 'Accept Renewal' }}
				</button>

				<button
					v-if="requestMade"
					class="w-full bg-accent text-white py-2.5 sm:py-3.5 px-6 text-sm sm:text-base rounded-lg font-semibold hover:bg-accent-dark shadow-md hover:shadow-lg transition-all duration-200 active:scale-98"
					@click="rejectRenewal"
					:disabled="loading"
				>
					Reject Renewal
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import RenewPolicyModal from '../../newportal/templates/others/RenewPolicyModal.vue';
import CenteredModal from '../../newportal/templates/others/centered-modal.vue';
import {
	acceptRenewalRequest,
	makeRenewalRequest,
	rejectRenewalRequest, // ← you forgot to import this!
	FetchInvoiceDetails,
	FetchPolicyDetails,
} from '../../newportal/api/portal';

export default {
	components: {
		RenewPolicyModal,
		CenteredModal,
		ViewInvoice: () => import('../../newportal/main/view-invoice.vue'),
		InvoicePayment: () =>
			import('../../newportal/main/invoice-payment.vue'),
	},
	data() {
		return {
			start_date: '',
			remarks: '',
			requestMade: false,
			policySystemRef: '',
			errors: {
				start_date: '',
				remarks: '',
			},
		};
	},

	computed: {
		...mapGetters(['loading']),
		...mapGetters('quotations', ['selectClientPolicy']),

		// THIS IS THE CORRECT WAY - make it a computed property!
		selectedProduct() {
			// Always return an array to prevent v-for errors
			return Array.isArray(this.selectClientPolicy)
				? this.selectClientPolicy
				: this.selectClientPolicy
					? [this.selectClientPolicy]
					: [];
		},

		todayDate() {
			return new Date().toISOString().split('T')[0];
		},
	},

	methods: {
		...mapActions('invoices', [
			'setInvoiceDetails',
			'setClientInvoicesData',
		]),
		...mapActions('quotations', [
			'setClientPolicy',
			'setClientPoliciesData',
		]),

		async handleViewPolicy() {
			try {
				const res = await FetchPolicyDetails({
					policySystemRef: this.$route.params.policyId,
				});

				if (res.status && res.data) {
					this.setClientPolicy(res.data);
				}
			} catch (err) {
				const message =
					err.response.data.errors ||
					err.message ||
					'Failed to fetch policy details';

				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message,
					duration: 5000,
				});
			}
		},

		validateField(field) {
			this.errors[field] = '';

			if (field === 'start_date' && !this.start_date) {
				this.errors.start_date = 'Start date is required';
			}
			if (field === 'remarks' && !this.remarks) {
				this.errors.remarks = 'Remarks are required';
			}
		},

		validateAllFields() {
			this.validateField('start_date');
			this.validateField('remarks');
			return !this.errors.start_date && !this.errors.remarks;
		},

		formatCurrency(amount) {
			if (!amount && amount !== 0) return 'KES 0.00';
			return `KES ${Number(amount).toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`;
		},

		async initiatePayment() {
			if (!this.validateAllFields()) {
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Validation Error',
					message: 'Please fill in all required fields correctly.',
					duration: 4000,
				});
				return;
			}

			this.$store.commit('loading', false);

			try {
				// Safely get the first product
				const product = this.selectedProduct[0];
				if (!product) {
					throw new Error('No policy data available');
				}

				const payload = {
					policySystemRef: product.policySystemRef,
					renewalStartDate: this.start_date,
					renewalRemarks: this.remarks,
				};

				const res = await makeRenewalRequest(payload);

				if (res.status) {
					this.policySystemRef = res.data.policyRenewalSystemRef;
					this.requestMade = true;

					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Renewal Request Sent',
						message: 'Please accept or reject the renewal.',
						duration: 4000,
					});
				}
			} catch (err) {
				const errMessage =
					err.response.data.errors ||
					err.response.data.message ||
					'Could not initiate renewal';
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Failed',
					message: errMessage || 'Could not initiate renewal',
					duration: 5000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},

		async acceptRenewal() {
			this.$store.commit('loading', true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1500));
				const res = await acceptRenewalRequest({
					policyRenewalSystemRef: this.policySystemRef,
				});
				if (res.status) {
					const ref = { invoiceSystemRef: res.data.invoiceSystemRef };
					const response = await FetchInvoiceDetails(ref);
					if (response.status) {
						this.setInvoiceDetails(response.data);
						this.$store.commit('showRenewPolicyModal', false);
						this.$store.commit('showInvoice', true); // make sure this mutation exists
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Success',
							message: 'Proceed to payment',
							duration: 3000,
						});
					}
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},

		async rejectRenewal() {
			this.$store.commit('loading', true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1500));
				const res = await rejectRenewalRequest({
					policyRenewalSystemRef: this.policySystemRef,
				});
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Renewal rejected',
						duration: 3000,
					});
					this.$store.commit('showRenewPolicyModal', false);
					this.requestMade = false;
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$store.commit('loading', false);
			}
		},
	},

	mounted() {
		this.handleViewPolicy();
		// Now safe - selectedProduct is reactive computed
		console.log('Selected Product:', this.selectedProduct);
	},
};
</script>

<style scoped>
.active\:scale-98:active {
	transform: scale(0.98);
}
</style>
